import React, {createContext, useContext, useMemo, useState, ReactNode} from 'react';

/**
 * MobileHeaderActionsContext — то, что страница хочет показать/настроить в
 * мобильном AppNavbar (он заменяет PageHeader на xs/sm):
 *   - title — динамический заголовок (имя машины и т.п.). null → навбар берёт
 *     статический титул роута через usePageTitle.
 *   - actions — кнопки/контролы справа от заголовка.
 *   - seamlessBottom — страница сообщает, что СРАЗУ под навбаром идёт свой
 *     отделяющий элемент (полоса табов). Навбар гасит свою линию, чтобы не
 *     было двух параллельных границ. По умолчанию false.
 *
 * Страница задаёт значения в useEffect и ОБЯЗАТЕЛЬНО очищает их при
 * размонтировании, иначе настройки "протекут" на следующую страницу.
 */
interface MobileHeaderActionsContextType {
    title: ReactNode | null;
    setTitle: (node: ReactNode | null) => void;
    actions: ReactNode | null;
    setActions: (node: ReactNode | null) => void;
    seamlessBottom: boolean;
    setSeamlessBottom: (value: boolean) => void;
}

/** Заглушка для дефолтного значения контекста (до монтирования провайдера). */
const noop = (): void => undefined;

const MobileHeaderActionsContext = createContext<MobileHeaderActionsContextType>({
    title: null,
    setTitle: noop,
    actions: null,
    setActions: noop,
    seamlessBottom: false,
    setSeamlessBottom: noop,
});

export const useMobileHeaderActions = () => useContext(MobileHeaderActionsContext);

export const MobileHeaderActionsProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [title, setTitle] = useState<ReactNode | null>(null);
    const [actions, setActions] = useState<ReactNode | null>(null);
    const [seamlessBottom, setSeamlessBottom] = useState(false);

    const value = useMemo(
        () => ({title, setTitle, actions, setActions, seamlessBottom, setSeamlessBottom}),
        [title, actions, seamlessBottom],
    );

    return (
        <MobileHeaderActionsContext.Provider value={value}>
            {children}
        </MobileHeaderActionsContext.Provider>
    );
};